import Joi from 'joi'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../../../UI/Btn'
import TextField from '../../../../../../UI/TextField'

import SettingHint from '../SettingHint'
import TemplateLinks from '../TemplateLinks'

import styles from '../style.module.scss'

/*
 * Meldungen auf Deutsch und feldbezogen (Anforderung #30). Ohne eigene Texte
 * zeigt Joi den Rohtext samt Feldpfad an („supporterRequestReminders.
 * frequencyDays must be greater than or equal to 1") — englisch und für die
 * Person vor der Maske nicht verwertbar.
 */
const schema = Joi.object({
	supporterRequestReminders: Joi.object().keys({
		frequencyDays: Joi.number().integer().min(1).max(90).required().messages({
			'number.base': 'Bitte einen Takt in Tagen angeben.',
			'number.integer': 'Bitte einen Takt in ganzen Tagen angeben.',
			'number.min': 'Bitte einen Takt von mindestens 1 Tag angeben.',
			'number.max': 'Bitte einen Takt von höchstens 90 Tagen angeben.',
			'any.required': 'Bitte einen Takt in Tagen angeben.'
		}),
		maxReminders: Joi.number().integer().min(0).max(10).required().messages({
			'number.base': 'Bitte eine Anzahl an Erinnerungen angeben.',
			'number.integer': 'Bitte eine ganze Zahl an Erinnerungen angeben.',
			'number.min': 'Bitte 0 oder mehr Erinnerungen angeben.',
			'number.max': 'Bitte höchstens 10 Erinnerungen angeben.',
			'any.required': 'Bitte eine Anzahl an Erinnerungen angeben.'
		})
	})
})

/**
 * Takt der Erinnerungen an unbeantwortete Betreuungsanfragen.
 *
 * Fragt eine Junior Company eine Expert:in an und bleibt die Antwort aus, erinnert
 * das System — nicht die Schüler:innen. Nach der letzten Erinnerung gilt die
 * Anfrage als abgelehnt, damit die Junior Company weitersuchen kann statt auf
 * eine Antwort zu warten, die nicht mehr kommt.
 */
export default function SupporterRequestReminders(props) {
	const { t } = useTranslation()
	const {
		register,
		reset,
		handleSubmit,
		formState: { errors }
	} = useForm({ resolver: joiResolver(schema) })

	const onSubmit = form => {
		props
			.updateOptions({
				name: 'supporterRequestReminders',
				// „enabled" ist bewusst nicht im Formular: abschalten lässt sich der
				// Lauf über 0 Erinnerungen. Der vorhandene Wert bleibt erhalten.
				value: {
					...(props.form?.supporterRequestReminders || {}),
					frequencyDays: Number(form.supporterRequestReminders.frequencyDays),
					maxReminders: Number(form.supporterRequestReminders.maxReminders)
				}
			})
			.then(() => toast.success(t('Options was updated')))
	}

	useEffect(() => {
		const value = props.form?.supporterRequestReminders || {}
		reset({
			supporterRequestReminders: {
				frequencyDays: value.frequencyDays ?? 3,
				maxReminders: value.maxReminders ?? 3
			}
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])

	return (
		<form id="supporterRequestReminders" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>Erinnerungen an offene Betreuungsanfragen</h3>
				<SettingHint
					example="Beispiel: alle 3 Tage erinnern, nach 3 Erinnerungen gilt die Anfrage als abgelehnt — die Junior Company weiß nach spätestens 12 Tagen, woran sie ist."
					warning="Der Lauf findet einmal täglich in der Nacht statt. Mit 0 Erinnerungen wird eine unbeantwortete Anfrage beim nächsten fälligen Lauf sofort abgelehnt; erinnert wird dann nicht mehr."
				>
					Beantwortet eine Unterstützer:in eine Anfrage einer Junior Company weder mit Zusage noch mit Absage,
					erinnert das System automatisch. Schüler:innen müssen also nicht selbst nachfassen; sie sehen an der
					Anfrage, wie oft das System bereits erinnert hat. Nach der letzten Erinnerung wird die Anfrage
					abgelehnt und die Junior Company kann jemand anderen anfragen.
				</SettingHint>
				<TemplateLinks items={[{ channel: 'email', key: 'SUPPORTER_REQUEST_REMINDER' }]} />
				<div className={styles.sectionContent}>
					<div className={styles.flexContainer}>
						<TextField
							className={styles.field}
							label="Erinnerung alle [x] Tage"
							mb={20}
							register={register('supporterRequestReminders.frequencyDays')}
							error={errors.supporterRequestReminders?.frequencyDays}
						/>
						<TextField
							className={styles.field}
							label="Anzahl Erinnerungen bis zur automatischen Ablehnung"
							mb={20}
							register={register('supporterRequestReminders.maxReminders')}
							error={errors.supporterRequestReminders?.maxReminders}
						/>
					</div>
					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

SupporterRequestReminders.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}
